import { Box } from '@mui/material';
import { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon, Row, Text } from '@/components/ui';
import { useDialogHelpers } from '@/components/feedback';
import { deletePhotoIcon, galleryIcon, makePhoto } from '@/assets';
import { RotateLeft } from '@mui/icons-material';
import { IconButton } from '@mui/material';
import { ImageGallery } from './ImageGallery';
import {
  UploadProgress,
  useUploadProgress,
} from '@/components/ui/UploadProgress';
import { uploadPhotoAsBase64WithProgress } from '@/services/uploadService';
import type { PhotoData } from '../types';
import { sitesApi } from '../services';

interface Props {
  photoList: PhotoData[];
  onPhotosChanged: (updatedPhotoList: PhotoData[]) => void;
  deletePhoto: (photoId: number) => Promise<void>;
  siteId: number;
  isLoadingPhoto?: boolean;
}

export const PhotoEdit = ({
  photoList,
  onPhotosChanged,
  deletePhoto: deletePhotoFromServer,
  siteId,
  isLoadingPhoto = false,
}: Props) => {
  const { t } = useTranslation();
  const { confirmDelete } = useDialogHelpers();
  const [images, setImages] = useState<PhotoData[]>([]);
  const [showImageID, setShowImageID] = useState<number | null>(null);
  const [rotatingImages, setRotatingImages] = useState<Set<number>>(new Set());
  const rotationTimeouts = useRef<Map<number, NodeJS.Timeout>>(new Map());
  const maxNumber = 5;

  // Refs for file input elements - replaces imperative DOM access
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);

  // Upload progress management
  const { uploads, addUpload, updateUpload, removeUpload } =
    useUploadProgress();

  useEffect(() => {
    // Sync with photoList when it changes (e.g., new photos uploaded)
    // Don't sync during rotation to preserve local rotated images
    if (rotatingImages.size === 0) {
      setImages(photoList);
    }
  }, [photoList]);

  // Cleanup rotation timeouts on unmount
  useEffect(() => {
    return () => {
      rotationTimeouts.current.forEach(timeout => clearTimeout(timeout));
      rotationTimeouts.current.clear();
    };
  }, []);

  const onChange = async (file: File) => {
    // Create unique upload ID
    const uploadId = `upload-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

    // Add to upload progress tracking
    addUpload({
      id: uploadId,
      fileName: file.name,
      status: 'compressing',
    });

    try {
      await uploadPhotoAsBase64WithProgress(file, siteId, {
        onCompressionStart: () => {
          updateUpload(uploadId, { status: 'compressing', progress: 0 });
        },
        onCompressionComplete: result => {
          updateUpload(uploadId, {
            status: 'uploading',
            progress: 50,
            originalSize: result.originalSize,
            compressedSize: result.compressedSize,
          });
        },
        onUploadStart: () => {
          updateUpload(uploadId, { status: 'uploading', progress: 60 });
        },
        onProgress: progress => {
          // Map upload progress to 60-100% range
          const mappedProgress = 60 + progress * 0.4;
          updateUpload(uploadId, { progress: mappedProgress });
        },
        onUploadComplete: response => {
          updateUpload(uploadId, { status: 'success', progress: 100 });

          // Add to local state
          if (response.isSuccess && response.result?.data) {
            const newImage: PhotoData = {
              dataUrl: response.result.data.base64Image.startsWith('data:')
                ? response.result.data.base64Image
                : `data:image/jpeg;base64,${response.result.data.base64Image}`,
              id: response.result.data.id,
            };

            // Use functional update to prevent stale closure issues during parallel uploads
            setImages(prevImages => {
              const updatedImages = [...prevImages, newImage];
              // Notify parent of the complete updated photo list
              onPhotosChanged(updatedImages);
              return updatedImages;
            });
          }

          // Remove from upload tracking after 3 seconds
          setTimeout(() => removeUpload(uploadId), 3000);
        },
        onError: error => {
          updateUpload(uploadId, {
            status: 'error',
            progress: 0,
            error: error.message,
          });
        },
      });
    } catch (error) {
      console.error('Upload failed:', error);
    }
  };

  const handleDeletePhoto = async (id: number) => {
    const confirmed = await confirmDelete(t('are_you_shure_delete_picture'));

    if (confirmed) {
      try {
        // Delete from server via API
        await deletePhotoFromServer(id);

        // Remove from local state only after successful server deletion
        // Use functional update to prevent stale closure issues during parallel operations
        setImages(prevImages => {
          const updatedImages = prevImages.filter(item => item.id !== id);
          // Notify parent of the complete updated photo list
          onPhotosChanged(updatedImages);
          return updatedImages;
        });
      } catch (error) {
        console.error('Failed to delete photo:', error);
      }
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length && e.target.files[0]) {
      onChange(e.target.files[0]);
      // Reset the input value to allow selecting the same file again
      e.target.value = '';
    }
  };

  const handleCameraCapture = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length && e.target.files[0]) {
      onChange(e.target.files[0]);
      // Reset the input value to allow selecting the same file again
      e.target.value = '';
    }
  };

  const handlePhotoRotated = async (
    oldPhotoId: number,
    newPhotoData: PhotoData
  ) => {
    // The newPhotoData already contains the rotated image from the backend
    // Just update the local state with the rotated version
    setImages(prevImages => {
      const updatedImages = prevImages.map(img =>
        img.id === oldPhotoId ? newPhotoData : img
      );
      onPhotosChanged(updatedImages);
      return updatedImages;
    });

    // No need to refresh all photos - the rotated image is already in the response
  };

  // Debounced rotation function to prevent multiple simultaneous requests
  const debouncedPreviewRotate = useCallback(
    async (imageId: number) => {
      // Check if this image is already being rotated
      if (rotatingImages.has(imageId)) {
        return;
      }

      // Add to rotating set
      setRotatingImages(prev => new Set(prev).add(imageId));

      try {
        // Create upload ID for progress tracking
        const uploadId = `rotation-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

        // Add to upload progress tracking
        addUpload({
          id: uploadId,
          fileName: `rotated-${imageId}.jpg`,
          status: 'uploading',
        });

        updateUpload(uploadId, { progress: 30 });

        // Call backend to rotate the image
        const response = await sitesApi.rotatePhoto(siteId, imageId);

        if (response.isSuccess && response.result?.data) {
          updateUpload(uploadId, { status: 'success', progress: 100 });

          const newImage: PhotoData = {
            dataUrl: response.result.data.base64Image.startsWith('data:')
              ? response.result.data.base64Image
              : `data:image/jpeg;base64,${response.result.data.base64Image}`,
            id: imageId,
          };

          // Update local state with rotated image
          setImages(prevImages => {
            const updatedImages = prevImages.map(img =>
              img.id === imageId ? newImage : img
            );
            onPhotosChanged(updatedImages);
            return updatedImages;
          });

          // Remove from rotating set - no need to refresh all photos
          setRotatingImages(prev => {
            const newSet = new Set(prev);
            newSet.delete(imageId);
            return newSet;
          });

          setTimeout(() => removeUpload(uploadId), 3000);
        } else {
          throw new Error(response.error?.message || 'Failed to rotate image');
        }
      } catch (error) {
        console.error('Failed to rotate image from preview:', error);

        // On error, immediately remove from rotating set
        setRotatingImages(prev => {
          const newSet = new Set(prev);
          newSet.delete(imageId);
          return newSet;
        });
      }
    },
    [
      rotatingImages,
      addUpload,
      updateUpload,
      removeUpload,
      onPhotosChanged,
      siteId,
    ]
  );

  // Wrapper function for preview rotation with timeout debounce
  const handlePreviewRotate = useCallback(
    (imageId: number) => {
      // Clear existing timeout for this image
      const existingTimeout = rotationTimeouts.current.get(imageId);
      if (existingTimeout) {
        clearTimeout(existingTimeout);
      }

      // Set new timeout
      const timeoutId = setTimeout(() => {
        debouncedPreviewRotate(imageId);
        rotationTimeouts.current.delete(imageId);
      }, 300); // 300ms debounce

      rotationTimeouts.current.set(imageId, timeoutId);
    },
    [debouncedPreviewRotate]
  );

  return (
    <>
      <Box
        className='App'
        sx={{
          // opacity: isLoadingPhoto ? 0.5 : 1,
          marginTop: '1rem',
          marginBottom: '1rem',
        }}
      >
        <Row spacing={'0.25rem'} sx={{ alignItems: 'flex-end' }}>
          <Text
            sx={{ color: 'black', mt: '0.25rem' }}
          >{`${t('site_photos_title_edit_site')}`}</Text>
          <Text sx={{ color: 'black', mt: '0.25rem' }}>({images.length})</Text>
        </Row>

        <Box sx={{ maxWidth: '100%' }}>
          <Row
            sx={{
              padding: '0.25rem',
              display: 'flex',
              flexWrap: 'wrap',
              gap: '1rem',
            }}
          >
            {images.length < maxNumber && (
              <Box
                onClick={() =>
                  !isLoadingPhoto && cameraInputRef.current?.click()
                }
                sx={{
                  flex: '1 1 calc(33.33% - 1rem)',
                  minHeight: '6.25rem',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  background: '#F8F9FA',
                  boxShadow:
                    '0px 1px 3px 0px #0000001A, 0px 1px 2px 0px #0000001A',
                  borderRadius: '0.5rem',
                }}
              >
                <Icon sx={{ mx: '0.5rem' }} src={makePhoto} />
              </Box>
            )}

            {images.length < maxNumber && (
              <Box
                onClick={() =>
                  !isLoadingPhoto && galleryInputRef.current?.click()
                }
                sx={{
                  flex: '1 1 calc(33.33% - 1rem)',
                  minHeight: '6.25rem',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  background: '#F8F9FA',
                  boxShadow:
                    '0px 1px 3px 0px #0000001A, 0px 1px 2px 0px #0000001A',
                  borderRadius: '0.5rem',
                }}
              >
                <Icon sx={{ mx: '0.5rem' }} src={galleryIcon} />
              </Box>
            )}

            {images.map(image => (
              <Box
                key={image.id}
                className='image-item'
                sx={{
                  flex: '1 1 calc(33.33% - 1rem)',
                  height: '6.25rem',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  background: '#F8F9FA',
                  boxShadow:
                    '0px 1px 3px 0px #0000001A, 0px 1px 2px 0px #0000001A',
                  borderRadius: '0.5rem',
                  position: 'relative',
                }}
                onClick={() => {
                  setShowImageID(image.id);
                }}
              >
                <img
                  style={{
                    padding: '1rem 0',
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain',
                  }}
                  src={image.dataUrl}
                  alt=''
                />
                {/* Delete button */}
                <Box
                  sx={{
                    position: 'absolute',
                    right: '-0.125rem',
                    top: '-0.125rem',
                  }}
                  onClick={event => {
                    event.stopPropagation();
                    handleDeletePhoto(image.id);
                  }}
                >
                  <Icon src={deletePhotoIcon} />
                </Box>

                {/* Rotate button */}
                <Box
                  sx={{
                    position: 'absolute',
                    left: '-0.125rem',
                    top: '-0.125rem',
                  }}
                  onClick={event => {
                    event.stopPropagation();
                    handlePreviewRotate(image.id);
                  }}
                >
                  <IconButton
                    size='small'
                    disabled={rotatingImages.has(image.id)}
                    sx={{
                      width: '24px',
                      height: '24px',
                      backgroundColor: '#F5F5F5',
                      color: rotatingImages.has(image.id) ? '#999' : '#333',
                      '&:hover': {
                        backgroundColor: '#E0E0E0',
                      },
                      '&:disabled': {
                        backgroundColor: '#F5F5F5',
                        color: '#999',
                      },
                    }}
                    title={
                      rotatingImages.has(image.id)
                        ? t('rotating_image')
                        : t('rotate_image')
                    }
                  >
                    <RotateLeft fontSize='inherit' />
                  </IconButton>
                </Box>
              </Box>
            ))}

            {/* Empty boxes to maintain grid layout */}
            {Array.from({ length: (3 - ((images.length + 2) % 3)) % 3 }).map(
              (_, i) => (
                <Box
                  key={`empty-${i}`}
                  sx={{
                    flex: '1 1 calc(33.33% - 1rem)',
                    height: '6.25rem',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    background: '#F8F9FA',
                    boxShadow:
                      '0px 1px 3px 0px #0000001A, 0px 1px 2px 0px #0000001A',
                    borderRadius: '0.5rem',
                    visibility: 'hidden',
                  }}
                />
              )
            )}
          </Row>
        </Box>

        {/* Hidden file inputs - using refs instead of imperative DOM access */}
        <input
          ref={cameraInputRef}
          type='file'
          accept='image/*'
          capture='environment'
          style={{ display: 'none' }}
          onChange={handleCameraCapture}
        />

        <input
          ref={galleryInputRef}
          type='file'
          accept='image/*'
          style={{ display: 'none' }}
          onChange={handleFileUpload}
        />
      </Box>

      {/* Image Gallery - outside opacity container */}
      {showImageID !== null && (
        <ImageGallery
          imageId={showImageID}
          photoList={images}
          changeSelectedImage={setShowImageID}
          onPhotoRotated={handlePhotoRotated}
          siteId={siteId}
        />
      )}

      {/* Upload Progress - outside opacity container */}
      <UploadProgress
        uploads={uploads}
        onRetry={id => {
          console.log('Retry upload not implemented yet for ID:', id);
          // TODO: Implement retry logic
        }}
        onCancel={id => {
          removeUpload(id);
        }}
      />
    </>
  );
};
